/**
 * Resources-by-establishment plot: filters, fetch, empty state or stacked bar.
 */

import * as React from 'react';
import {
  HorizontalStackedBarChart,
  type BarChartData,
} from '@/components/common/stats/HorizontalStackedBarChart';
import {
  CHART_LABELS,
  CHART_SKELETON_HEIGHT,
  RESOURCE_SERIES_COLORS,
  type ChartHeaderFilterProps,
} from '@/components/charts/chart-configs';
import { ResourcesChartFilters } from '@/components/charts/resources/ResourcesChartFilters';
import {
  buildResourcesChartView,
  chartHeightForCount,
  hasChartSeriesData,
  resolveResourcesChartFilterVisibility,
  resourcesChartCountTitle,
} from '@/components/charts/resources/resources-chart-utils';
import { useDashboardCharts } from '@/hooks/useDashboardCharts';
import { useInterfaceElements } from '@/hooks/useInterfaceElements';
import { getErrorMessage } from '@/utils/errors-utils';
import {
  DEFAULT_DASHBOARD_STATS_DATE_FILTERS,
  buildDashboardStatsDateQuery,
  type DashboardStatsDateFilterState,
} from '@/utils/dashboard-stats-date-range-utils';

/** Empty plot when no resource series match the filters. */
function renderEmpty(height: number): React.ReactNode {
  return (
    <div
      className="flex flex-col items-center justify-center gap-2 text-muted-foreground rounded-xl border bg-card/60"
      style={{ height }}
    >
      <p className="text-sm font-medium">{CHART_LABELS.resourcesEmpty}</p>
      <p className="text-xs opacity-70">{CHART_LABELS.resourcesEmptyHint}</p>
    </div>
  );
}

/** Resources stacked bar with its own date / establishment filters. */
export const ResourcesChart: React.FC<ChartHeaderFilterProps> = ({
  showFilters,
  onHasFiltersChange,
}) => {
  const { elements: dashboardElements = [] } = useInterfaceElements('dashboard');
  const visibility = resolveResourcesChartFilterVisibility(dashboardElements);
  const [dateFilters, setDateFilters] = React.useState<DashboardStatsDateFilterState>(
    DEFAULT_DASHBOARD_STATS_DATE_FILTERS,
  );

  const chartsApiQuery = React.useMemo(
    () => buildDashboardStatsDateQuery(dateFilters),
    [dateFilters],
  );
  const { data: chartsData, isLoading, isError } = useDashboardCharts(chartsApiQuery);

  const { chartData, displayCount } = React.useMemo(
    () => buildResourcesChartView(chartsData?.resourcesByEstablishment as BarChartData | undefined),
    [chartsData],
  );
  const height = chartHeightForCount(displayCount);
  const countTitle = resourcesChartCountTitle(displayCount);

  React.useEffect(() => {
    onHasFiltersChange(!isLoading && (isError || visibility.hasAnyFilter));
  }, [isLoading, isError, visibility.hasAnyFilter, onHasFiltersChange]);

  function renderFilters(): React.ReactNode {
    if (!showFilters) return null;
    return (
      <ResourcesChartFilters
        filters={dateFilters}
        onChange={setDateFilters}
        visibility={visibility}
      />
    );
  }

  function renderSubtitle(): React.ReactNode {
    if (isLoading || isError || displayCount <= 0) return null;
    return (
      <p className="text-xs text-muted-foreground mb-2">
        {countTitle} · {CHART_LABELS.resourcesSubtitle}
      </p>
    );
  }

  function renderPlot(): React.ReactNode {
    if (isLoading) {
      return (
        <div
          className="rounded-xl border bg-card p-4 shadow-sm animate-pulse"
          style={{ height: CHART_SKELETON_HEIGHT }}
        />
      );
    }
    if (isError) {
      return (
        <div className="flex items-center justify-center h-40 text-muted-foreground text-sm rounded-xl border bg-card">
          {getErrorMessage('loadChartFailed')}
        </div>
      );
    }
    if (!hasChartSeriesData(chartData)) return renderEmpty(height);
    return (
      <HorizontalStackedBarChart
        data={chartData}
        height={height}
        seriesColors={RESOURCE_SERIES_COLORS}
      />
    );
  }

  return (
    <>
      {renderFilters()}
      {renderSubtitle()}
      {renderPlot()}
    </>
  );
};
